import React, { FunctionComponent, useState, useEffect } from 'react'
import _isEmpty from 'lodash/isEmpty'
import { CSSTransition } from 'react-transition-group'
import validator from 'validator'

import { Modal, Loading } from '../../../..'

import { Website } from '../../../../../typings/website'

import fieldAlertTransitions from '../../../../../utils/transitions/fieldAlert.module.css'
import styles from './styles.module.css'

interface AddFormProps {
  close(): void
  addWebsite(website: Website): void
}

interface FieldErrors {
  name?: string
  url?: string
  description?: string
}

const AddForm: FunctionComponent<AddFormProps> = ({ close, addWebsite }) => {
  const [submitted, setSubmitted] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [enableSubmit, setEnableSubmit] = useState(false)

  const [fields, setFields] = useState<Website>({
    name: '',
    url: '',
  })
  const [displayErrors, setDisplayErrors] = useState<FieldErrors>({
    name: '',
    url: '',
  })

  // Name field errors
  useEffect(() => {
    if (_isEmpty(displayErrors.name) && _isEmpty(fields.name)) {
      setDisplayErrors(prev => ({
        ...prev,
        name: 'Please enter a site name',
      }))
    }
  }, [fields.name, displayErrors.name])

  // URL field errors
  useEffect(() => {
    if (_isEmpty(displayErrors.url)) {
      if (_isEmpty(fields.url)) {
        setDisplayErrors(prev => ({
          ...prev,
          url: 'Please enter a URL',
        }))
      } else if (!validator.isURL(fields.url)) {
        setDisplayErrors(prev => ({
          ...prev,
          url: 'Invalid URL',
        }))
      } else if (!validator.isURL(fields.url, { require_protocol: true })) {
        setDisplayErrors(prev => ({
          ...prev,
          url: 'http:// or https:// is required',
        }))
      }
    }
  }, [fields.url, displayErrors.url])

  // Reset if errors are addressed
  useEffect(() => {
    if (
      _isEmpty(displayErrors.name) &&
      _isEmpty(displayErrors.url) &&
      submitted
    ) {
      setSubmitted(false)
    }
  }, [submitted, displayErrors])

  // Disable submit until form is valid
  useEffect(() => {
    if (
      !submitted &&
      !isSubmitting &&
      _isEmpty(displayErrors.name) &&
      _isEmpty(displayErrors.url)
    ) {
      setEnableSubmit(true)
    } else {
      setEnableSubmit(false)
    }
  }, [displayErrors.name, displayErrors.url, isSubmitting, submitted])

  const handleSubmit = async e => {
    e.preventDefault()

    if (
      !submitted &&
      !isSubmitting &&
      _isEmpty(displayErrors.name) &&
      _isEmpty(displayErrors.url)
    ) {
      setIsSubmitting(true)
      await addWebsite(fields)
      await setTimeout(close, 5000)
    }
  }

  return (
    <Modal isOpen close={close} className={styles.modal}>
      <div className={styles.container}>
        <div className={styles.header}>
          <h2>Add a Website</h2>
        </div>
        <div className={styles.content}>
          <label htmlFor="name">Site Name:</label>
          <input
            id="name"
            defaultValue={fields.name}
            onChange={e => {
              const name = e.currentTarget.value

              // clear any existing field errors on change
              if (!_isEmpty(displayErrors.name)) {
                setDisplayErrors(prev => ({
                  ...prev,
                  name: '',
                }))
              }

              setFields(prev => ({
                ...prev,
                name,
              }))
            }}
            data-rv="website-edit-name"
          />

          <label htmlFor="url">URL:</label>
          <input
            id="url"
            defaultValue={fields.url}
            onChange={e => {
              const url = e.currentTarget.value

              // clear any existing field errors on change
              if (!_isEmpty(displayErrors.url)) {
                setDisplayErrors(prev => ({
                  ...prev,
                  url: '',
                }))
              }

              setFields(prev => ({
                ...prev,
                url,
              }))
            }}
            data-rv="website-edit-url"
          />

          <label htmlFor="description">Description:</label>
          <textarea
            id="description"
            defaultValue={fields.description}
            onChange={e => {
              const description = e.currentTarget.value

              setFields(prev => ({
                ...prev,
                description,
              }))
            }}
            data-rv="website-edit-description"
          />
        </div>
        <div className={styles.buttonContainer}>
          {isSubmitting ? (
            <button className={styles.loading} disabled>
              <Loading className={styles.spinner} />
            </button>
          ) : (
            <>
              <CSSTransition
                in={
                  !_isEmpty(displayErrors.name) || !_isEmpty(displayErrors.url)
                }
                timeout={600}
                classNames={fieldAlertTransitions}
                appear
                mountOnEnter
                unmountOnExit
              >
                <div className={styles.error} data-rv="website-edit-error">
                  {displayErrors.name || displayErrors.url}
                </div>
              </CSSTransition>
              <button
                className={styles.button}
                onClick={async e => {
                  setSubmitted(true)
                  await handleSubmit(e)
                }}
                disabled={enableSubmit === false}
                data-rv="website-edit-submit"
              >
                Save Changes
              </button>
            </>
          )}
        </div>
      </div>
    </Modal>
  )
}

export default AddForm
